import { useEffect, useState } from "react";
import { ArrowUpRight, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionKicker } from "./Reveal";
import { scrollToId } from "@/lib/constants";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

export const DropCountdown = () => {
  const { t, lang } = useLang();
  const [drop, setDrop] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setDrop(res.data)).catch(() => {});
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!drop || !drop.drop_enabled || !drop.next_drop_at) return null;
  const target = new Date(drop.next_drop_at).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - now;
  const live = diff <= 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const title = (lang === "de" ? drop.drop_title_de : drop.drop_title_en) || t.drop.title;
  const cells = [
    [days, t.drop.days],
    [hours, t.drop.hours],
    [mins, t.drop.mins],
    [secs, t.drop.secs],
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/notify", { email });
      setDone(true);
      toast.success(t.notify.success);
    } catch {
      toast.error(t.notify.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="drop-countdown-section" className="border-b border-edge bg-paperalt">
      <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
        <Reveal>
          <SectionKicker>{t.drop.kicker}</SectionKicker>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl" data-testid="drop-title">
              {live ? <span className="italic text-volt">{t.drop.live}</span> : title}
            </h2>
            {!live && (
              <div className="grid grid-cols-4 border border-edge bg-surface" data-testid="drop-timer">
                {cells.map(([val, label], i) => (
                  <div key={i} className="flex flex-col items-center border-r border-edge px-4 py-3 last:border-r-0 sm:px-7 sm:py-4">
                    <span className="font-mono text-2xl font-bold tabular-nums text-volt sm:text-4xl" data-testid={`drop-cell-${i}`}>
                      {pad(val)}
                    </span>
                    <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-mutedink">{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {live ? (
            <div className="mt-6">
              <p className="max-w-md text-sm leading-relaxed text-mutedink">{t.drop.liveSub}</p>
              <button
                data-testid="drop-live-cta"
                onClick={() => scrollToId("katalog")}
                className="group mt-5 flex items-center gap-2 bg-volt px-6 py-3.5 font-grotesk text-sm font-bold uppercase tracking-widest text-obsidian transition-colors duration-300 hover:bg-volt-hover"
              >
                {t.drop.cta}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          ) : (
            <div className="mt-8 flex max-w-xl flex-col gap-4 border border-edge bg-surface p-5" data-testid="notify-box">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center border border-volt text-volt">
                  <Bell size={14} />
                </span>
                <div className="min-w-0">
                  <div className="font-grotesk text-sm font-semibold uppercase tracking-widest text-ink">{t.notify.title}</div>
                  <p className="mt-1 text-xs leading-relaxed text-mutedink">{t.notify.sub}</p>
                </div>
              </div>
              {done ? (
                <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-okgreen" data-testid="notify-success">
                  <Check size={14} /> {t.notify.done}
                </div>
              ) : (
                <form onSubmit={subscribe} className="flex flex-col gap-2 sm:flex-row" data-testid="notify-form">
                  <input
                    data-testid="notify-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.notify.placeholder}
                    className="w-full flex-1 border border-edge bg-obsidian px-3.5 py-2.5 font-grotesk text-sm text-ink placeholder:text-mutedink/60 focus:border-volt focus:outline-none"
                  />
                  <button
                    data-testid="notify-submit-button"
                    type="submit"
                    disabled={sending}
                    className="shrink-0 bg-volt px-4 py-2.5 font-grotesk text-xs font-bold uppercase tracking-widest text-obsidian transition-colors duration-300 hover:bg-volt-hover disabled:opacity-50"
                  >
                    {sending ? t.notify.sending : t.notify.button}
                  </button>
                </form>
              )}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
